import type { OperationsIntakeRowDto, OperationsLensKey, OperationsPenKey, OperationsStageKey, OperationsStageRowDto } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { ATTENTION_CLASS, deltaClass, formatDelta, HIDDEN_STAGE, INTAKE_ROW_LABEL, intakeRowTip, lensLabel, STAGE_LABEL, STAGE_ORDER } from "../operationsLabels";
import { AttentionBar, AttentionLegend } from "./AttentionBar";
import { LensTag } from "./LensTags";

interface Props {
  rows: OperationsStageRowDto[];
  /** The lens the rows were cut with: one colour per bar, legend "Stuck items · N of M open". */
  lens?: OperationsLensKey;
  /** The second lens (who holds the pen, 2026-10-28): the bars keep their attention colours, the counts are cut. */
  pen?: OperationsPenKey;
  lensedOpenNow?: number;
  openNow?: number;
  onDrill: (stage: OperationsStageKey) => void;
  /** The "Request (Intake)" row above the stages; absent until the API carries it. */
  intake?: OperationsIntakeRowDto;
  onDrillIntake?: () => void;
}

const STAGE_TIP =
  "Where each open request sits, from its own dates: not completed = Request Processing; completed but work order not submitted = Completed to WO Submit; submitted, not approved = WO Submit to Approve. " +
  "The rows plus the note under them add up to Open now.";
const HIDDEN_TIP =
  "Every date is set but FBO still says not Completed. These stay open until someone fixes the status in FBO.";
const DOD_TIP =
  "Change since yesterday 08:00 ET. Approximate: FBO keeps no history, so yesterday is rebuilt from today's dates.";

/**
 * Panel 3: open items per stage, segmented by attention, with an approximate DoD. The API also
 * returns a by-FBO-status split under Request Processing; it is deliberately not rendered
 * (Rafael, 2026-10-09: one row per stage keeps the card readable).
 */
export function WipByStage({ rows, lens, pen, lensedOpenNow, openNow, onDrill, intake, onDrillIntake }: Props) {
  const label = lensLabel(lens, pen);
  const byKey = new Map(rows.map((r) => [r.stage, r]));
  const ordered = STAGE_ORDER.map((k) => byKey.get(k)).filter((r): r is OperationsStageRowDto => !!r);
  const hidden = byKey.get(HIDDEN_STAGE);
  const hiddenCount = hidden?.split.total ?? 0;
  const max = Math.max(1, intake?.available ? intake.split.total : 0, ...ordered.map((r) => r.split.total));
  const intakeLegend = intake?.available && label ? ` · plus ${intake.split.total} of ${intake.openNowAll} at intake` : "";

  return (
    <section className="ops-panel ops-span-4" aria-labelledby="ops-stage-h">
      <h3 id="ops-stage-h">
        Work in progress by stage
        <InfoTooltip inline text={STAGE_TIP} />
        <LensTag lens={lens} pen={pen} />
      </h3>
      <div className="ops-stages">
        <div className="ops-stage head">
          <span className="nm">Stage</span>
          <span />
          <span className="ct">Open</span>
          <span className="dod">
            DoD, approx.
            <InfoTooltip inline text={DOD_TIP} />
          </span>
        </div>
        {intake && <IntakeRow row={intake} max={max} lens={lens} label={label} onDrill={onDrillIntake} />}
        {ordered.map((s) => (
          <StageRows key={s.stage} row={s} max={max} lens={lens} label={label} onDrill={onDrill} />
        ))}
      </div>
      {hiddenCount > 0 && (
        <p className="ops-stage-note" data-stage={HIDDEN_STAGE}>
          {hiddenCount} more open {hiddenCount === 1 ? "item" : "items"} awaiting close: every date set, FBO status not yet Completed
          <InfoTooltip inline text={HIDDEN_TIP} />
          {" · "}
          <button type="button" className="ops-link-num" onClick={() => onDrill(HIDDEN_STAGE)}>list</button>
        </p>
      )}
      <div style={{ marginTop: 12 }}>
        {lens ? (
          <div className="ops-legend">
            <span><i className={`ops-sw lens-${ATTENTION_CLASS[lens]}`} />{label} items · {lensedOpenNow ?? 0} of {openNow ?? 0} open{intakeLegend}</span>
          </div>
        ) : (
          <>
            <AttentionLegend />
            {pen && <div className="ops-legend"><span><i className={`ops-sw lens-pen ${pen}`} />{label} items · {lensedOpenNow ?? 0} of {openNow ?? 0} open{intakeLegend}</span></div>}
          </>
        )}
      </div>
    </section>
  );
}

function StageRows({ row, max, lens, label: lensed, onDrill }: { row: OperationsStageRowDto; max: number; lens?: OperationsLensKey; label: string | null; onDrill: Props["onDrill"] }) {
  const label = STAGE_LABEL[row.stage];
  const hasDelta = row.split.total > 0 || row.previousTotal > 0;
  const title = lensed ? `${lensed} items in ${label}: click to list` : `Open items in ${label}: click to list`;
  // "is-empty", not "empty": the global .empty class is the centred empty-state block.
  return (
    <div className={`ops-stage${row.split.total ? "" : " is-empty"}`} data-stage={row.stage}>
      <span className="nm" title={label}>{label}</span>
      <AttentionBar split={row.split} max={max} title={title} lens={lens} onClick={() => onDrill(row.stage)} />
      <span className="ct" title={row.pendingMedianBd != null ? `Median pending ${row.pendingMedianBd} bd on the first open task or work order` : undefined}>{row.split.total}</span>
      <span className="dod">
        {hasDelta ? <span className={`ops-delta ${deltaClass(row.delta)}`}>{formatDelta(row.delta)}</span> : <span className="muted">—</span>}
      </span>
    </div>
  );
}

/**
 * The intake row: same look as a stage, but a separate population (not in Open now). Unavailable
 * (sync off, read failed) renders the row with no bar and the reason as its tooltip, so the scrum
 * sees that intake is missing rather than a silently shorter card.
 */
function IntakeRow({ row, max, lens, label: lensed, onDrill }: { row: OperationsIntakeRowDto; max: number; lens?: OperationsLensKey; label: string | null; onDrill?: () => void }) {
  const label = INTAKE_ROW_LABEL;
  if (!row.available) {
    return (
      <div className="ops-stage is-empty" data-stage="intake" title={row.reason ?? "Intake is unavailable"}>
        <span className="nm">{label}<InfoTooltip inline text={row.reason ?? "Intake is unavailable."} /></span>
        <span className="muted" style={{ fontSize: 12 }}>unavailable</span>
        <span className="ct">—</span>
        <span className="dod"><span className="muted">—</span></span>
      </div>
    );
  }
  const hasDelta = row.split.total > 0 || row.previousTotal > 0;
  const title = lensed ? `${lensed} items at intake: click to list` : "Open items at intake: click to list";
  return (
    <div className={`ops-stage${row.split.total ? "" : " is-empty"}`} data-stage="intake">
      <span className="nm" title={label}>{label}<InfoTooltip inline text={intakeRowTip(row.stuckAfterBd, row.blockedAfterBd, row.hideAfterUntouchedBd)} /></span>
      <AttentionBar split={row.split} max={max} title={title} lens={lens} onClick={onDrill} />
      <span className="ct">{row.split.total}</span>
      <span className="dod">
        {hasDelta ? <span className={`ops-delta ${deltaClass(row.delta)}`}>{formatDelta(row.delta)}</span> : <span className="muted">—</span>}
      </span>
    </div>
  );
}
